/**
 * This file acts as wrapper to analytics.js from Segment.
 * This gives us greated flexibility in customizing tracking,
 * replacing the tracking provider, etc at a later date.
 *
 * Extend with new methods as needed.
 */

import { merge } from 'lodash-es';
import type { DeepPartial } from 'types/utility';
import debug from 'utils/debug';
import { addSessionId as addAnalyticsSessionId } from './session';
import type {
  AnalyticsIdentityTraits,
  GroupTraits,
  Options,
  PageProperties,
  TrackingProperties,
} from './types';

const enrichOptions = (options: Options = {}): Options => {
  const optionsWithUserId: DeepPartial<Options> = {
    context: {
      memory_user_id: window.BOOTSTRAPPED_DATA.user.id.toString(),
    },
  };
  return addAnalyticsSessionId(merge({}, options, optionsWithUserId));
};

/** identify
 * Ref: https://segment.com/docs/sources/website/analytics.js/#identify
 */
const identify = (
  email: string,
  traits: AnalyticsIdentityTraits,
  options: Options = {},
  callback?: () => void
) => {
  if (!window.analytics) {
    return false;
  }
  debug.template.analytics('Identify', { email, traits, options });
  window.analytics.identify(email, traits, enrichOptions(options), callback);
};

/** TRACK */

type EventName = string;

/** Track
 * Ref: https://segment.com/docs/sources/website/analytics.js/#track
 */
const track = <T extends EventName>(
  event: T,
  properties: Partial<TrackingProperties>,
  options: Options = {},
  callback?: () => void
) => {
  if (!window.analytics) {
    return false;
  }
  debug.template.analytics(event, { event, properties, options, subtopic: 'Track' });
  window.analytics.track(event, properties, enrichOptions(options), callback);
};

/**
 * Track, but for TIC events /w better type safety.
 */
const ticTrackEventNames = [
  'TIC+AI Disabled',
  'TIC+AI Enabled',
  'TIC+AI Changed Preset',
  'TIC+AI Reset Everything',
  'TIC+AI Reject Prediction',
  'TIC+AI Click Magic AI Button',
  'TIC+AI Click Timeline Prediction',
  'TIC+AI Click Timesheet Prediction',
  'TIC+AI Click TA Autofill Note',
  'TIC+AI Click Autofill Suggestion Notes',
  'TIC+AI Accept generated timesheet',
  'TA2 Requested AI Timesheet',
  // 'TA2 Generated AI Timesheet' — emitted from ai_timely Lambda (TT-5024)
  'TA2 Submitted AI Generated Timesheet',
  'TA2 Timesheet Email Click',
  'TA2 Updated AI Timesheet',
  'TA2 Requested Note Regeneration',
  // 'TA2 Received Regenerated Notes' — emitted from ai_timely Lambda (TT-5024)
] as const;
export type TicTrackEventName = (typeof ticTrackEventNames)[number];
const trackTic = (...args: Parameters<typeof track<TicTrackEventName>>) => {
  return track(...args);
};

/** PAGE */

/** Page
 * Ref: https://segment.com/docs/sources/website/analytics.js/#page
 * properties: {title, path, url, referrer, and search} are set automatically
 */
const page = (
  category: string,
  name: string,
  properties: PageProperties,
  options: Options = {}
) => {
  if (!window.analytics) {
    return false;
  }
  const url = properties?.path || window.location.pathname;
  debug.template.analytics(url, { category, name, properties, options, subtopic: 'Page View' });
  window.analytics.page(category, name, properties, enrichOptions(options));
};

/** Alias
 * Ref: https://segment.com/docs/sources/website/analytics.js/#alias
 */

const alias = (
  userEmail: string,
  previousUserEmail: string,
  options: Options,
  callback: () => void
) => {
  if (!window.analytics) {
    return false;
  }
  debug.template.analytics('Alias', { userEmail, previousUserEmail, options });
  window.analytics.alias(userEmail, previousUserEmail, enrichOptions(options), callback);
};

/** Group
 * The group method associates an identified user with a company, organization, project,
 * workspace, team, tribe, platoon, assemblage, cluster, troop, gang, party, society or
 * any other name you came up with for the same concept.
 * This is useful for tools like:
 * Intercom, Preact and Totango, as it ties the user to a group of other users.
 * Ref: https://segment.com/docs/sources/website/analytics.js/#group
 */

const group = (
  workspace: { id: number; name: string },
  traits: GroupTraits,
  options?: Options,
  callback?: () => void
) => {
  if (!window.analytics) {
    return false;
  }
  debug.template.analytics('Group', { workspace, traits, options });
  window.analytics.group(
    String(workspace.id),
    {
      ...traits,
      // Apparently you need to send group_type and name on every call
      // Marius can explain more
      name: workspace.name,
      group_type: 'Account Name',
    },
    enrichOptions(options),
    callback
  );
};

export default {
  identify,
  track,
  trackTic,
  page,
  alias,
  group,
};
